import * as echarts from 'echarts';
import moment from 'moment';
import { TOOLTIP_LABEL_FORMAT, TOOLTIP_LABEL_FORMAT_RT } from '@utils/constants';
import { adjustColor, STACK_COLOR_CODE } from '../constants';
import { generateFillerData, getPlotInterval } from '@components/metrics-panel-hz/utils/format';
import { humanize } from '@utils';

export const buildStackedOption = (chartData, realTime) => {
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const { value } = params[0];
        const formattedDate = moment(value[0]).format(
          realTime ? TOOLTIP_LABEL_FORMAT_RT : TOOLTIP_LABEL_FORMAT,
        );
        let tooltip = `<div class="tooltip-label">${formattedDate}</div>`;
        for (let i = params.length - 1; i >= 0; i--) {
          const { seriesName, value, marker } = params[i];
          const formattedValue = echarts.format.addCommas(value[1]);
          tooltip += `<div class="tooltip-label" style="display:flex; align-items:center; justify-content: space-between"><span>${marker} ${seriesName}</span> <strong>${formattedValue}</strong></div>`;
        }
        return tooltip;
      },
    },
    legend: {
      bottom: 0,
      lineStyle: {
        width: 0,
      },
      orient: 'horizontal',
      data: chartData?.map((i) => i.name).reverse(),
    },
    grid: {
      top: 10,
      bottom: 60,
      left: 50,
      right: 35,
    },
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          formatter: (value) => {
            return moment(value).format(realTime ? 'HH:mm:ss' : 'HH:mm');
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        splitNumber: 2,
        axisLabel: {
          formatter: (value) => humanize(value),
        },
      },
    ],
    series: chartData,
  };
};

export const getStackedTimeSeries = ({
  data,
  groupBy,
  metric,
  interval,
  colorMap,
  series,
}: {
  data: any[];
  groupBy: string;
  metric: string;
  interval: any;
  colorMap: Record<string, string>;
  series: string[];
}) => {
  const plotInterval = getPlotInterval(interval, data?.[0]?.results);
  const fillerData = generateFillerData(plotInterval);
  const dataMap = data?.reduce((group, curr) => {
    group[curr[groupBy]] = curr.results.reduce((map, item) => {
      map[moment(item.timestamp).toISOString()] = item[metric];
      return map;
    }, {});
    return group;
  }, {});

  return series.map((type) => {
    return {
      name: type,
      type: 'line',
      stack: 'Total',
      symbol: 'none',
      areaStyle: {},
      color: colorMap[type],
      data: fillerData.map(([timestamp, value]) => {
        return [timestamp, dataMap[type]?.[timestamp] ?? value];
      }),
    };
  });
};

export const getCodeColorMap = (codes: string[] = []) =>
  Object.values(
    codes.reduce(
      (map, code) => {
        let color = '#000';
        if (code && String(code).charAt(0) && STACK_COLOR_CODE[String(code).charAt(0)]) {
          color = STACK_COLOR_CODE[String(code).charAt(0)];
        }
        if (map[color]) {
          const darkerColor = adjustColor(color, map[color].length * 10);
          map[color] = [...map[color], { color: darkerColor, code }];
        } else {
          map[color] = [{ color, code }];
        }
        return map;
      },
      {} as Record<string, { code: string; color: string }[]>,
    ),
  )
    .flat()
    .reduce(
      (colorMap, curr) => {
        colorMap[curr.code] = curr.color;
        return colorMap;
      },
      {} as Record<string, string>,
    );
